export interface LocationData {
  latitude: number | null;
  longitude: number | null;
  streetAddress?: string | null;
  city?: string | null;
  country?: string | null;
  region?: string | null;
  ip?: string | null;
  accuracy?: number | null;
  source: 'gps' | 'ip' | 'merged' | 'none';
  timestamp?: number;
}

export interface LocationOptions {
  gpsTimeout?: number;
  gpsHighAccuracy?: boolean;
  ipTimeout?: number;
  includeReverseGeocode?: boolean;
  cacheTTL?: number;
}

const DEFAULTS: Required<LocationOptions> = {
  gpsTimeout: 7000,
  gpsHighAccuracy: true,
  ipTimeout: 5000,
  includeReverseGeocode: true,
  cacheTTL: 1000 * 60 * 5, // 5 minutes
};

// Timeout wrapper
const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('Timeout')), ms);
    promise.then((v) => {
      clearTimeout(t);
      resolve(v);
    }).catch((e) => {
      clearTimeout(t);
      reject(e);
    });
  });

// localStorage cache
const STORAGE_KEY = 'location_cache_v1';

const readCache = (): { value: LocationData | null; expires: number } | null => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeCache = (data: LocationData, ttl: number) => {
  if (typeof localStorage === 'undefined') return;
  try {
    const payload = { value: { ...data, timestamp: Date.now() }, expires: Date.now() + ttl };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {}
};

// Reverse geocoding with Google Maps (more accurate, includes street address)
const reverseGeocode = async (
  lat: number,
  lon: number
): Promise<{ streetAddress: string | null; city: string | null; region: string | null; country: string | null }> => {
  try {
    // Use Supabase edge function for secure API key handling
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    
    const res = await fetch(`${supabaseUrl}/functions/v1/reverse-geocode`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseKey}`,
      },
      body: JSON.stringify({ latitude: lat, longitude: lon }),
    });
    
    if (!res.ok) throw new Error('Geocoding failed');
    const data = await res.json();
    return {
      streetAddress: data.streetAddress || null,
      city: data.city || null,
      region: data.region || null,
      country: data.country || null
    };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return { streetAddress: null, city: null, region: null, country: null };
  }
};

// GPS location
const getGPS = (opts: Required<LocationOptions>): Promise<LocationData> =>
  new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return reject(new Error('Geolocation unsupported'));
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        let streetAddress = null;
        let city = null;
        let region = null;
        let country = null;
        
        if (opts.includeReverseGeocode) {
          const rev = await reverseGeocode(lat, lon);
          streetAddress = rev.streetAddress;
          city = rev.city;
          region = rev.region;
          country = rev.country;
        }
        
        resolve({
          latitude: lat,
          longitude: lon,
          streetAddress,
          city,
          region,
          country,
          ip: null,
          accuracy: pos.coords.accuracy || null,
          source: 'gps',
          timestamp: Date.now()
        });
      },
      (err) => reject(err),
      {
        enableHighAccuracy: opts.gpsHighAccuracy,
        timeout: opts.gpsTimeout,
        maximumAge: 0
      }
    );
  });

// IP lookup
const getIP = async (opts: Required<LocationOptions>): Promise<LocationData> => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), opts.ipTimeout);
  
  try {
    const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
    clearTimeout(id);
    if (!res.ok) throw new Error('IP lookup failed');
    const d = await res.json();
    
    return {
      latitude: d.latitude || null,
      longitude: d.longitude || null,
      streetAddress: null, // IP lookup doesn't provide street address
      city: d.city || null,
      region: d.region || null,
      country: d.country_name || null,
      ip: d.ip || null,
      accuracy: 50000, // ~50km estimate for IP-based location
      source: 'ip',
      timestamp: Date.now()
    };
  } catch {
    clearTimeout(id);
    return {
      latitude: null,
      longitude: null,
      city: null,
      region: null,
      country: null,
      ip: null,
      accuracy: null,
      source: 'none',
      timestamp: Date.now()
    };
  }
};

// Merge GPS and IP data
const mergeData = (gps: LocationData | null, ip: LocationData | null): LocationData => {
  const now = Date.now();
  
  if (gps && gps.latitude !== null && gps.longitude !== null) {
    const out: LocationData = { ...gps };
    // Enhance with IP data if GPS reverse geocode failed
    if ((!out.city || !out.country) && ip) {
      out.streetAddress = out.streetAddress || null;
      out.city = out.city || ip.city || null;
      out.region = out.region || ip.region || null;
      out.country = out.country || ip.country || null;
      out.ip = ip.ip;
    }
    out.source = 'gps';
    out.timestamp = now;
    return out;
  }

  if (ip && ip.latitude !== null && ip.longitude !== null) {
    return { ...ip, source: 'ip', timestamp: now };
  }

  return {
    latitude: null,
    longitude: null,
    streetAddress: null,
    city: null,
    country: null,
    region: null,
    ip: null,
    accuracy: null,
    source: 'none',
    timestamp: now
  };
};

// Main function: GPS-first with IP fallback and caching
export const getPreciseLocation = async (opts: LocationOptions = {}): Promise<LocationData> => {
  const mergedOpts: Required<LocationOptions> = { ...DEFAULTS, ...opts };

  // Check for location consent
  const consent = typeof localStorage !== 'undefined' ? localStorage.getItem('location_consent') : null;
  
  // If user has explicitly denied location tracking, return empty data
  if (consent === 'denied') {
    return {
      latitude: null,
      longitude: null,
      streetAddress: null,
      city: null,
      country: null,
      region: null,
      ip: null,
      accuracy: null,
      source: 'none',
      timestamp: Date.now()
    };
  }

  // Check cache first
  try {
    const cached = readCache();
    if (cached && cached.expires && cached.value && Date.now() < cached.expires) {
      return { ...cached.value };
    }
  } catch {}

  // Try GPS first (will trigger permission prompt)
  let gpsResult: LocationData | null = null;
  try {
    gpsResult = await withTimeout(getGPS(mergedOpts), mergedOpts.gpsTimeout);
  } catch {
    // GPS failed/denied/timeout - continue to IP fallback
  }

  // Try IP fallback
  let ipResult: LocationData | null = null;
  try {
    ipResult = await getIP(mergedOpts);
  } catch {
    ipResult = null;
  }

  // Merge results
  const final = mergeData(gpsResult, ipResult);

  // Additional reverse geocode if needed
  if (
    mergedOpts.includeReverseGeocode &&
    final.latitude &&
    final.longitude &&
    (!final.city || !final.country) &&
    final.source === 'ip'
  ) {
    try {
      const rev = await reverseGeocode(final.latitude, final.longitude);
      final.streetAddress = final.streetAddress || rev.streetAddress;
      final.city = final.city || rev.city;
      final.region = final.region || rev.region;
      final.country = final.country || rev.country;
    } catch {}
  }

  // Cache the result
  try {
    writeCache(final, mergedOpts.cacheTTL);
  } catch {}

  return final;
};
